import React from "react";

import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import Axios from "axios";

import { sweetConfirm } from "components/sweet-alert/sweetConfirm";
import { INavbarData, IShortcut } from "./navbar-shortcuts";
import Icon from "components/icon";

interface IDraggableShortcuts {
    navbar?: IShortcut[];
    setNavbarData: React.Dispatch<React.SetStateAction<INavbarData[] | null | undefined>>;
}

export const DraggableShortcuts = (props: IDraggableShortcuts) => {
    const { navbar, setNavbarData } = props;

    const { t } = useTranslation();

    const getItemStyle = (isDragging, draggableStyle) => ({
        userSelect: "none",
        ...draggableStyle,
    });

    const getListStyle = (isDraggingOver) => ({
        display: "flex",
        listStyle: "none",
        backgroundColor: isDraggingOver ? "#725ec311" : "transparent",
        padding: "5px 0",
        borderRadius: "5px",
        border: isDraggingOver ? "2px dashed #725ec344" : "2px dashed #725ec333",
        position: "relative",
        top: "-1px", // offset because of the (1/2) border
        left: "-2px", // offset because of the border
    });

    const onDragEnd = async (result) => {
        const { source, destination } = result;

        // dropped outside the list
        if (!result.destination) {
            // delete
            if (!(await sweetConfirm("Delete", "Do you want to remove this shortcut?"))) return;

            const result = [...(navbar || [])];

            result.splice(source.index, 1);

            setNavbarData([{ navBar: result }]);

            Axios.post("nav-bar", { navBar: result });
            return;
        }

        // dropped into droppable component
        switch (source.droppableId) {
            case destination.droppableId:
                // reorder
                const result = [...(navbar || [])];
                const [removed] = result.splice(source.index, 1);

                result.splice(destination.index, 0, removed);

                setNavbarData([{ navBar: result }]);

                Axios.post("nav-bar", { navBar: result });
                break;

            default:
                break;
        }
    };

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="SHORTCUTS-ORDER" direction="horizontal">
                {(provided, snapshot) => (
                    <ul {...provided.droppableProps} ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
                        {navbar?.map((shortcut: any, i) => {
                            const { url, label, icon } = shortcut;
                            let isActive = false;

                            if (window.location.pathname == "/" && url == "/") {
                                isActive = true;
                            } else {
                                isActive = url[1] ? window.location.pathname == url : false;
                            }

                            return (
                                <Draggable key={`shortcut-${i}`} draggableId={`shortcut-${i}`} index={i}>
                                    {(provided, snapshot) => (
                                        <li
                                            key={i}
                                            className="navbar-shortcut"
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                                        >
                                            <div className="draggable-disabled-btn mr-10" title={t(label)}>
                                                <span
                                                    style={{ display: "inline" }}
                                                    className={`nav-shortcut-icon mr-5 ${isActive ? "active-shortcut" : ""}`}
                                                >
                                                    {icon && <Icon name={icon} />}
                                                </span>
                                                <span
                                                    style={{ display: "inline" }}
                                                    className={`nav-shortcut-label ${isActive ? "active-shortcut" : ""}`}
                                                >
                                                    {t(label)}
                                                </span>
                                            </div>
                                        </li>
                                    )}
                                </Draggable>
                            );
                        })}
                        {provided.placeholder}
                    </ul>
                )}
            </Droppable>
        </DragDropContext>
    );
};
